/**
 * AddDelayItemDialog - Dialog para adicionar/editar item de delay
 * 
 * Permite configurar tempo de espera entre mensagens (1-60 segundos)
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Timer } from 'lucide-react';

// =====================================================
// INTERFACES
// =====================================================

interface AddDelayItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (seconds: number) => void;
  initialValue?: number;
}

// =====================================================
// COMPONENTE
// =====================================================

export function AddDelayItemDialog({
  open,
  onOpenChange,
  onSave,
  initialValue,
}: AddDelayItemDialogProps) {
  const [seconds, setSeconds] = useState(3);

  // Reset ao abrir dialog
  useEffect(() => {
    if (open) {
      setSeconds(initialValue || 3);
    }
  }, [open, initialValue]);

  // Handler de salvar
  const handleSave = () => {
    if (seconds >= 1 && seconds <= 60) {
      onSave(seconds);
    }
  };

  // Handler de input manual
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 60) {
      setSeconds(value);
    }
  };

  // Validação
  const isValid = seconds >= 1 && seconds <= 60;

  // Formatação do tempo
  const formatTime = (secs: number) => {
    if (secs < 60) {
      return `${secs} segundo${secs !== 1 ? 's' : ''}`;
    }
    return '1 minuto';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            {initialValue ? 'Editar Delay' : 'Adicionar Delay'}
          </DialogTitle>
          <DialogDescription>
            Configure o tempo de espera entre mensagens.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview visual */}
          <div className="flex flex-col items-center justify-center p-6 bg-muted rounded-lg">
            <Timer className="h-12 w-12 text-muted-foreground mb-3" />
            <span className="text-2xl font-bold">{formatTime(seconds)}</span>
            <span className="text-sm text-muted-foreground mt-1">
              de pausa
            </span>
          </div>

          {/* Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Tempo de espera</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={seconds}
                  onChange={handleInputChange}
                  className="w-16 text-center"
                />
                <span className="text-sm text-muted-foreground">seg</span>
              </div>
            </div>
            
            <Slider
              value={[seconds]}
              onValueChange={([value]) => setSeconds(value)}
              min={1}
              max={60}
              step={1}
              className="py-2"
            />
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1s</span>
              <span>30s</span>
              <span>60s</span>
            </div>
          </div>

          {/* Dica */}
          <p className="text-xs text-muted-foreground text-center">
            💡 Delays simulam comportamento humano e ajudam a evitar 
            detecção de spam pelo WhatsApp.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            {initialValue ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
